import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CustomerNav } from '../components/CustomerNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { Check, ArrowRight, ArrowLeft, Sparkles, MapPin, CalendarIcon, User, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeSlots = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00'
];

export default function Booking() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    service_id: serviceId || '',
    branch_id: '',
    booking_date: null,
    booking_time: '',
    special_requests: '',
    guest_count: 1,
    preferred_staff: '',
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: user?.phone || ''
  });

  const steps = [
    { id: 1, title: t('service'), icon: Sparkles },
    { id: 2, title: t('location'), icon: MapPin },
    { id: 3, title: t('date'), icon: CalendarIcon },
    { id: 4, title: t('yourDetails'), icon: User },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email,
        customer_phone: user.phone || prev.customer_phone
      }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [servicesRes, branchesRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/branches`)
      ]);
      setServices(servicesRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  };

  const selectedService = services.find(s => s.service_id === formData.service_id);
  const selectedBranch = branches.find(b => b.branch_id === formData.branch_id);

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!formData.service_id;
      case 2: return !!formData.branch_id;
      case 3: return !!formData.booking_date && !!formData.booking_time;
      case 4: return !!formData.customer_name && !!formData.customer_email && !!formData.customer_phone;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!canProceed()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const bookingData = {
        ...formData,
        booking_date: format(formData.booking_date, 'yyyy-MM-dd')
      };

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/bookings`, bookingData, { headers, withCredentials: true });
      
      toast.success('Booking submitted successfully! Check your email for confirmation.');
      navigate(user ? '/my-bookings' : '/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-sand">
        <CustomerNav />
        <div className="flex items-center justify-center pt-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-warm-sand ${isRTL ? 'rtl' : 'ltr'}`}>
      <CustomerNav />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-deep-charcoal mb-4">
              {t('bookYourExperience')}
            </h1>
            <p className="font-body text-lg text-deep-charcoal/60">
              {t('followSteps')}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-between items-center mb-12 relative">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-deep-charcoal/10 -translate-y-1/2" />
            {steps.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="relative flex flex-col items-center z-10">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isCompleted 
                        ? 'bg-sage text-white' 
                        : isActive 
                          ? 'bg-terracotta text-white shadow-lg' 
                          : 'bg-white text-deep-charcoal/40 border-2 border-deep-charcoal/10'
                    }`}
                  >
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`font-body text-xs mt-2 hidden sm:block ${
                    isActive ? 'text-terracotta font-medium' : 'text-deep-charcoal/50'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Step Content */}
          <Card className="bg-white rounded-3xl shadow-soft border-0">
            <CardContent className="p-8">
              {/* Step 1: Service Selection */}
              {currentStep === 1 && (
                <div className="space-y-6 animate-fade-in-up">
                  <h2 className="font-heading text-2xl font-semibold text-deep-charcoal">
                    {t('selectService')}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {services.map((service) => (
                      <button
                        key={service.service_id}
                        onClick={() => setFormData({ ...formData, service_id: service.service_id })}
                        data-testid={`select-service-${service.service_id}`}
                        className={`p-4 rounded-2xl text-left transition-all duration-300 border-2 ${
                          formData.service_id === service.service_id
                            ? 'border-terracotta bg-terracotta/5'
                            : 'border-deep-charcoal/10 hover:border-terracotta/50'
                        }`}
                      >
                        <div className={`flex items-start space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                          {service.image_url ? (
                            <img 
                              src={service.image_url} 
                              alt={service.name}
                              className="w-16 h-16 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-xl bg-terracotta/10 flex items-center justify-center">
                              <Sparkles className="w-6 h-6 text-terracotta/60" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="font-heading text-lg font-medium text-deep-charcoal">
                              {service.name}
                            </h3>
                            <p className="font-body text-sm text-deep-charcoal/60 line-clamp-1">
                              {service.description}
                            </p>
                            <p className="font-body text-terracotta font-semibold mt-1">
                              {formatPrice(service.price)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Branch Selection */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-fade-in-up">
                  <h2 className="font-heading text-2xl font-semibold text-deep-charcoal">
                    {t('selectLocation')}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {branches.map((branch) => (
                      <button
                        key={branch.branch_id}
                        onClick={() => setFormData({ ...formData, branch_id: branch.branch_id })}
                        data-testid={`select-branch-${branch.branch_id}`}
                        className={`p-6 rounded-2xl transition-all duration-300 border-2 ${isRTL ? 'text-right' : 'text-left'} ${
                          formData.branch_id === branch.branch_id
                            ? 'border-terracotta bg-terracotta/5'
                            : 'border-deep-charcoal/10 hover:border-terracotta/50'
                        }`}
                      >
                        <MapPin className={`w-6 h-6 mb-3 ${
                          formData.branch_id === branch.branch_id ? 'text-terracotta' : 'text-deep-charcoal/40'
                        }`} />
                        <h3 className="font-heading text-lg font-medium text-deep-charcoal">
                          {branch.name}
                        </h3>
                        <p className="font-body text-sm text-deep-charcoal/60 mt-1">
                          {branch.city}, {branch.country}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Date & Time */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-fade-in-up">
                  <h2 className="font-heading text-2xl font-semibold text-deep-charcoal">
                    {t('selectDateTime')}
                  </h2>
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div>
                      <Label className="font-body text-sm text-deep-charcoal/70 mb-3 block">
                        {t('chooseDate')}
                      </Label>
                      <Calendar
                        mode="single"
                        selected={formData.booking_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, booking_date: date }))}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        className="rounded-2xl border border-deep-charcoal/10 p-4"
                        data-testid="booking-calendar"
                      />
                    </div>
                    <div>
                      <Label className="font-body text-sm text-deep-charcoal/70 mb-3 block">
                        {t('chooseTime')}
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, booking_time: time }))}
                            data-testid={`time-slot-${time}`}
                            className={`py-2.5 px-3 rounded-xl font-body text-sm transition-all duration-200 ${
                              formData.booking_time === time
                                ? 'bg-terracotta text-white'
                                : 'bg-deep-charcoal/5 text-deep-charcoal hover:bg-terracotta/10'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Details */}
              {currentStep === 4 && (
                <div className="space-y-6 animate-fade-in-up">
                  <h2 className="font-heading text-2xl font-semibold text-deep-charcoal">
                    {t('yourDetails')}
                  </h2>
                  
                  {/* Booking Summary */}
                  <div className="bg-warm-sand/50 rounded-2xl p-6 mb-6">
                    <h3 className="font-heading text-lg font-medium text-deep-charcoal mb-4">
                      {t('bookingSummary')}
                    </h3>
                    <div className="space-y-2 font-body text-sm">
                      <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-deep-charcoal/60">{t('service')}:</span>
                        <span className="text-deep-charcoal font-medium">{selectedService?.name}</span>
                      </div>
                      <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-deep-charcoal/60">{t('location')}:</span>
                        <span className="text-deep-charcoal font-medium">{selectedBranch?.name}</span>
                      </div>
                      <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-deep-charcoal/60">{t('date')}:</span>
                        <span className="text-deep-charcoal font-medium">
                          {formData.booking_date && format(formData.booking_date, 'MMMM d, yyyy')}
                        </span>
                      </div>
                      <div className={`flex justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-deep-charcoal/60">{t('time')}:</span>
                        <span className="text-deep-charcoal font-medium">{formData.booking_time}</span>
                      </div>
                      <div className={`flex justify-between pt-2 border-t border-deep-charcoal/10 mt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-deep-charcoal font-medium">{t('total')}:</span>
                        <span className="text-terracotta font-bold text-lg">{formatPrice(selectedService?.price || 0)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-body text-sm text-deep-charcoal/70">
                        {t('fullName')} *
                      </Label>
                      <Input
                        id="name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        className="rounded-xl border-deep-charcoal/20 h-12"
                        placeholder={t('fullName')}
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-body text-sm text-deep-charcoal/70">
                        {t('emailAddress')} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        className="rounded-xl border-deep-charcoal/20 h-12"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-body text-sm text-deep-charcoal/70">
                        {t('phoneNumber')} *
                      </Label>
                      <Input
                        id="phone"
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                        className="rounded-xl border-deep-charcoal/20 h-12"
                        placeholder="+1 234 567 890"
                        data-testid="input-phone"
                      />
                    </div>
                    {selectedService?.category === 'kitchen' && (
                      <div className="space-y-2">
                        <Label htmlFor="guests" className="font-body text-sm text-deep-charcoal/70">
                          {t('numberOfGuests')}
                        </Label>
                        <Select
                          value={String(formData.guest_count)}
                          onValueChange={(value) => setFormData({ ...formData, guest_count: parseInt(value) })}
                        >
                          <SelectTrigger className="rounded-xl border-deep-charcoal/20 h-12" data-testid="select-guests">
                            <Users className={`w-4 h-4 text-deep-charcoal/40 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <SelectValue placeholder={t('numberOfGuests')} />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                              <SelectItem key={num} value={String(num)}>
                                {num} {num === 1 ? t('guest') : t('guests')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="requests" className="font-body text-sm text-deep-charcoal/70">
                      {t('specialRequests')}
                    </Label>
                    <Textarea
                      id="requests"
                      value={formData.special_requests}
                      onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                      className="rounded-xl border-deep-charcoal/20 min-h-[100px]"
                      placeholder={t('specialRequestsPlaceholder')}
                      data-testid="input-requests"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="staff" className="font-body text-sm text-deep-charcoal/70">
                      {t('preferredStaff')}
                    </Label>
                    <Input
                      id="staff"
                      value={formData.preferred_staff}
                      onChange={(e) => setFormData({ ...formData, preferred_staff: e.target.value })}
                      className="rounded-xl border-deep-charcoal/20 h-12"
                      placeholder={t('preferredStaffPlaceholder')}
                      data-testid="input-staff"
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className={`flex justify-between mt-10 pt-6 border-t border-deep-charcoal/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {currentStep > 1 ? (
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="rounded-full px-6 border-deep-charcoal/20"
                    data-testid="btn-back"
                  >
                    <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                    {t('back')}
                  </Button>
                ) : (
                  <div />
                )}
                
                {currentStep < 4 ? (
                  <Button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!canProceed()}
                    className="rounded-full px-8 bg-terracotta text-white hover:bg-terracotta-dark disabled:opacity-50"
                    data-testid="btn-next"
                  >
                    {t('continue')}
                    <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed() || submitting}
                    className="rounded-full px-8 bg-terracotta text-white hover:bg-terracotta-dark disabled:opacity-50"
                    data-testid="btn-submit"
                  >
                    {submitting ? t('submitting') : t('confirmBooking')}
                    <Check className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
